
import { useState, useEffect, useCallback } from 'react';
import { notificationManager } from '@/utils/notifications/NotificationManagerInstance';

interface AttendanceThresholds {
  absenceThreshold: number;
  latenessThreshold: number;
}

export const useAttendanceNotificationSettings = () => {
  const [thresholds, setThresholds] = useState<AttendanceThresholds>({
    absenceThreshold: 3,
    latenessThreshold: 4
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial settings from storage/config
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const config = notificationManager.getNotificationConfig('attendanceAlerts');
        
        if (config) {
          // Extract thresholds from config
          const absenceThreshold = config.threshold || 3;
          const latenessThreshold = config.latenessThreshold || 4;
          
          setThresholds({
            absenceThreshold,
            latenessThreshold
          });
        }
      } catch (err) {
        console.error('Failed to load attendance notification settings:', err);
        setError('Failed to load attendance notification settings');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, []);

  // Save updated thresholds
  const saveThresholds = useCallback(async (newThresholds: AttendanceThresholds): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Validate thresholds
      if (newThresholds.absenceThreshold < 1 || newThresholds.latenessThreshold < 1) {
        setError('Thresholds must be at least 1');
        return false;
      }
      
      // Get existing config
      const existingConfig = notificationManager.getNotificationConfig('attendanceAlerts');
      
      // Update config with new thresholds
      const updatedConfig = {
        ...existingConfig,
        threshold: newThresholds.absenceThreshold,
        latenessThreshold: newThresholds.latenessThreshold
      };
      
      // Save config
      const success = notificationManager.setNotificationConfig(updatedConfig, 'attendanceAlerts');
      
      if (success) {
        setThresholds(newThresholds);
        setError(null);
      } else {
        setError('Failed to save attendance notification settings');
      }
      
      return success;
    } catch (err) {
      console.error('Failed to save attendance notification settings:', err);
      setError('Failed to save attendance notification settings');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    thresholds,
    setThresholds: saveThresholds,
    isLoading,
    error
  };
};
